import { useMsal } from "@azure/msal-react";

import { useMe } from "@hooks/queries/use-me";

import { hasValidAppToken } from "@utils/appToken";

type UserProfile = {
  name?: string;
  email?: string;
  username?: string;
  homeAccountId?: string;
};

/**
 * Hook to get current user profile information.
 *
 * Internal staff sign in through Microsoft, so MSAL holds their name and email.
 * External users (doctors/experts, referrers, providers) sign in with an email
 * code and have no MSAL account of their own — for them the identity comes from
 * the `/me` DB record, which is populated for both auth paths (MDRS-334).
 *
 * An app token decides the question before MSAL gets a look in, matching
 * `useAuth` and `useProtectedRoute`: `index.jsx` runs `ssoSilent()` on every new
 * tab, so a leftover Microsoft session in the same browser can put an account in
 * `accounts` that is not who is signed in here. Reading MSAL first would name
 * the wrong person.
 *
 * `/me` is only queried on the external path — internal users already have
 * everything from MSAL, and an unauthenticated page would just earn a 401.
 * Disabling the query still reads through to cached data, so surfaces that
 * already fetched `/me` (e.g. the side navigation, which calls it un-gated)
 * cost nothing extra and keep this hook current.
 */
export const useUserProfile = (): UserProfile | null => {
  const { accounts } = useMsal();
  const isExternal = hasValidAppToken();
  const account = !isExternal && accounts.length > 0 ? accounts[0] : null;

  const { data: me } = useMe({ enabled: isExternal });
  const dbUser = me?.user;

  if (!account && !dbUser) {
    return null;
  }

  const email = account?.username || dbUser?.email;

  return {
    name: account?.name || dbUser?.name,
    email,
    username: email, // In MSAL, username often contains the email
    homeAccountId: account?.homeAccountId,
  };
};
